import { useEffect, useMemo } from 'react'

import { appMode } from '~/config'
import {
  selectIsIpRestricted,
  useIpRestrictionStore,
} from '~/stores/ipRestriction/ipRestriction'
import { useOfflineModeStore } from '~/stores/offlineMode/offlineMode'
import { useUserPlaceNodeStore } from '~/stores/place/place'
import { selectIsAuthed, useSessionStore } from '~/stores/session/session'
import { useBrowserSessionObservation } from './useBrowserSessionObservation'
import { useClearAllPersistedData } from './useClearAllPersistedData'
import { useDeviceAuthSession } from './useDeviceAuth'
import { useQueryMe } from './useQueryMe'

type Return = {
  /**
   * sync に失敗した場合のエラー情報。isSyncedNow が false の場合は常に null
   */
  error: Error | null
  /**
   * 認証済みかどうか。isSyncedNow が false の場合は、過去にログインした情報が端末に残っているかどうかのみで判断される
   */
  isAuthed: boolean
  /**
   * sync がいま行われたかどうか。オフラインモード時は false
   */
  isSyncedNow: boolean
}

/**
 * 前提として、Rump ログインにおいてログイン画面は Rump 側ドメインで管理されており、そこに遷移するために backend の `/auth/mobileLogin` へ location API で遷移する必要がある。
 * Rump 側でログインに成功すると、リダイレクトで backend で持っているリダイレクト先（現在は `/` トップページ）に戻ってくるが、この時に Cookie に harami_sid という名前でセッション ID がセットされることで認証ができる状態になる。
 * この hooks は、クライアントとしての内部 state のログイン状態を同期させるために、`/me` へリクエストを送った結果として認証主体 (Principal) の情報が取得できればログイン状態として更新するという役割のもの。
 *
 * オフラインモード時はログイン状態であるかどうかのみを判断する（特段の更新処理や API リクエストを行わない）
 * また、そのログイン状態についても、端末に過去にログインしたことがある情報を持っているかどうかでのみ判断する。
 *
 * @returns
 */
export const useSyncedAuthState = (): Return => {
  const shouldSyncNow = !useOfflineModeStore(state => state.offlineMode)
  const isAuthed = useSessionStore(selectIsAuthed)
  const principal = useSessionStore(state => state.principal)
  const rumpLogin = useSessionStore(state => state.rumpLogin)
  const newLoginUserPlaceNodeFetched = useUserPlaceNodeStore(
    state => state.newLoginUserPlaceNodeFetched,
  )
  const { clearAllPersistedData } = useClearAllPersistedData()

  // キオスク端末アカウント (Device) のセッション状態を先に確定させる。
  // kiosk モードかつ未ログインの場合のみ実行する。
  // - app モードでは自動でデバイスセッションを作らない。証明書のある端末でも
  //   個人ユーザーとしてのログインを妨げないため（Device は RBAC 対象外で権限が少ない）。
  // - ログイン済みならデバイスセッションの確認は不要のため実行しない。
  const deviceSessionCheckEnabled =
    shouldSyncNow && appMode.isKiosk && !isAuthed
  const { data: deviceAuthResult } = useDeviceAuthSession({
    enabled: deviceSessionCheckEnabled,
  })

  // IP アドレス制限でデバイスセッションを確立できない場合、/me は必ず失敗して
  // /login へ遷移してしまうため叩かない（グローバルの IP 制限エラー画面が表示される）。
  // 「IP 制限中かどうか」の情報源は zustand フラグに一本化する。deviceAuthResult の
  // 失敗理由（クエリキャッシュ）で判定すると、エラー画面の再試行が成功してもキャッシュが
  // 更新されず /me が無効のままになる不整合の余地があるため。
  const isIpRestricted = useIpRestrictionStore(selectIsIpRestricted)

  const {
    data: me,
    error,
    isLoading,
  } = useQueryMe({
    // /me は principal（User/Device）取得のため叩く。
    // デバイスセッションの確認・作成（useDeviceAuthSession）を行う場合のみ、その完了を待ってから叩く
    //（証明書のあるキオスク端末で Device セッションを先に確立するため）。
    // それ以外（ログイン済み・app モード・Rump ログインのリダイレクト直後など）は即実行し、/me の遅延を避ける。
    enabled:
      shouldSyncNow &&
      (!deviceSessionCheckEnabled || deviceAuthResult !== undefined) &&
      !isIpRestricted,
  })

  useBrowserSessionObservation(me)

  useEffect(() => {
    if (!shouldSyncNow) return
    if (!me) return

    // User / Device 共通で principal をセッションへ反映する。
    // NOTE: ログイン中に別アカウントで重ねてログインする可能性があるため、識別子が変わったら
    // 別アカウントの情報と混ざらないよう明示的に localStorage をクリアする。
    // 識別子は User=user.uuid / Device=krn（Device は uuid を持たない）。
    // user.uuid は v0→v1 移行でも保持されるため、移行済み User を誤ってクリアしない。
    const currentIdentity = me.principal.user?.uuid ?? me.principal.krn
    const storedIdentity = principal?.user?.uuid ?? principal?.krn
    if (currentIdentity !== storedIdentity) {
      clearAllPersistedData()
    }

    rumpLogin({
      type: me.principal.type,
      krn: me.principal.krn,
      name: me.principal.name,
      company: me.company,
      user: me.principal.user,
    })
    newLoginUserPlaceNodeFetched(me.placeNodes)

    // NOTE: principal の用途はログイン時の localStorage クリア処理のみであり、これは me と shouldSyncNow によって制御されるべきなため、depsList から除外している
    // principal に依存するとログアウト時の localStorage クリア処理でもこの useEffect が動作してしまい、その場合は me が残っているため rumpLogin が実行されてしまう
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearAllPersistedData,
    me,
    newLoginUserPlaceNodeFetched,
    rumpLogin,
    shouldSyncNow,
    // principal,
  ])

  // NOTE: store にログイン情報がセットされて完了とする
  const _isAuthed = useMemo(() => {
    return shouldSyncNow ? !isLoading && isAuthed : isAuthed
  }, [isAuthed, isLoading, shouldSyncNow])

  return {
    error,
    isAuthed: _isAuthed,
    isSyncedNow: shouldSyncNow,
  }
}
